/**
 * TimeframeDropdown
 *
 * Compact popover dropdown for selecting a dashboard timeframe.
 * Replaces the cramped 9-option pill slider with a clean trigger button
 * and vertical list of 6 preset options.
 *
 * Note: TIMEFRAME_OPTIONS in dashboard.ts stays unchanged — ExportersCard
 * uses the full array. This component manages its own reduced set.
 */

import { useState } from "react";
import { Calendar, ChevronDown } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";

const DROPDOWN_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "7days", label: "Last 7 days" },
  { value: "30days", label: "Last 30 days" },
  { value: "thismonth", label: "This month" },
  { value: "90days", label: "Last 90 days" },
  { value: "all", label: "All time" },
] as const;

interface TimeframeDropdownProps {
  value: string;
  onChange: (value: string) => void;
}

export default function TimeframeDropdown({
  value,
  onChange,
}: TimeframeDropdownProps) {
  const [open, setOpen] = useState(false);

  const selectedLabel =
    DROPDOWN_OPTIONS.find((opt) => opt.value === value)?.label ?? "Last 30 days";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 text-xs font-medium"
        >
          <Calendar className="h-3.5 w-3.5" />
          {selectedLabel}
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-44 p-1">
        <div className="flex flex-col">
          {DROPDOWN_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 text-sm rounded-sm transition-colors ${
                value === opt.value
                  ? "bg-accent text-accent-foreground font-medium"
                  : "hover:bg-accent/50 text-foreground"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
